import type { ReactElement } from 'react'
import { useEffect, useState } from 'react'
import {
  flashActive,
  fundraisingActive,
  FundraisingFlashContent,
  LennyFlashContent,
  TwentyVentureCapitalFlashContent,
} from '~/config/flash'

type FlashCampaign = {
  id: 'lenny' | '20vc'
  entryPath: string
  utmCampaign: string
  remainingSessions: number
  Content: () => ReactElement
}

const FUNDRAISING_SEEN_KEY = 'fundraising-banner-seen'

type FlashCampaignKeys = {
  active: string
  session: string
  remaining: string
}

const DEFAULT_REMAINING_SESSIONS = 3

const CAMPAIGNS: FlashCampaign[] = [
  {
    id: '20vc',
    entryPath: '/20VC',
    utmCampaign: '20vc-april-2026',
    remainingSessions: DEFAULT_REMAINING_SESSIONS,
    Content: TwentyVentureCapitalFlashContent,
  },
  {
    id: 'lenny',
    entryPath: '/lenny',
    utmCampaign: 'lenny-march-2026',
    remainingSessions: DEFAULT_REMAINING_SESSIONS,
    Content: LennyFlashContent,
  },
]

const buildKeys = (campaignId: FlashCampaign['id']): FlashCampaignKeys => ({
  active: `${campaignId}-banner-active`,
  session: `${campaignId}-banner-session`,
  remaining: `${campaignId}-banner-remaining-sessions`,
})

const normalize = (value: string | null) => value?.toLowerCase() ?? ''

const matchesCampaign = (
  campaign: FlashCampaign,
  pathname: string,
  params: URLSearchParams,
): boolean => {
  const normalizedPath = pathname.toLowerCase()
  const utmSourceValues = new Set(
    params.getAll('utm_source').map((value) => value.toLowerCase()),
  )
  const utmMedium = normalize(params.get('utm_medium'))
  const utmCampaign = normalize(params.get('utm_campaign'))

  const matchesPath = normalizedPath === campaign.entryPath.toLowerCase()
  const matchesUtm =
    utmSourceValues.has('podcast') &&
    utmMedium === 'sponsorship' &&
    (utmCampaign === campaign.utmCampaign ||
      utmSourceValues.has(campaign.utmCampaign))

  return matchesPath || matchesUtm
}

export const Flash = () => {
  const [activeCampaign, setActiveCampaign] = useState<
    FlashCampaign | 'fundraising' | null
  >(null)

  useEffect(() => {
    if (!flashActive) return

    if (typeof window === 'undefined') return

    const { pathname, search } = window.location
    const params = new URLSearchParams(search)

    const tryActivate = (campaign: FlashCampaign, isEntry: boolean) => {
      const keys = buildKeys(campaign.id)
      const remainingRaw = window.localStorage.getItem(keys.remaining)
      const remaining = remainingRaw ? Number(remainingRaw) : 0

      if (isEntry) {
        window.sessionStorage.setItem(keys.session, '1')
        window.sessionStorage.setItem(keys.active, '1')
        window.localStorage.setItem(
          keys.remaining,
          String(Math.max(campaign.remainingSessions - 1, 0)),
        )
        setActiveCampaign(campaign)
        return true
      }

      if (
        remaining > 0 &&
        window.sessionStorage.getItem(keys.session) !== '1'
      ) {
        window.sessionStorage.setItem(keys.session, '1')
        window.sessionStorage.setItem(keys.active, '1')
        window.localStorage.setItem(
          keys.remaining,
          String(Math.max(remaining - 1, 0)),
        )
        setActiveCampaign(campaign)
        return true
      }

      return false
    }

    const showCampaign = (campaign?: FlashCampaign) => {
      if (campaign) {
        setActiveCampaign(campaign)
      } else if (fundraisingActive) {
        setActiveCampaign('fundraising')
      } else {
        setActiveCampaign(null)
      }
    }

    const entryCampaign = CAMPAIGNS.find((campaign) =>
      matchesCampaign(campaign, pathname, params),
    )

    if (entryCampaign && tryActivate(entryCampaign, true)) {
      return
    }

    if (
      fundraisingActive &&
      window.localStorage.getItem(FUNDRAISING_SEEN_KEY) !== '1'
    ) {
      window.localStorage.setItem(FUNDRAISING_SEEN_KEY, '1')
      showCampaign()
      return
    }

    for (const campaign of CAMPAIGNS) {
      if (tryActivate(campaign, false)) {
        return
      }
    }

    const resumedCampaign = CAMPAIGNS.find(
      (campaign) =>
        window.sessionStorage.getItem(buildKeys(campaign.id).active) === '1',
    )

    showCampaign(resumedCampaign)
  }, [])

  if (!activeCampaign) return null
  const ActiveContent =
    activeCampaign === 'fundraising'
      ? FundraisingFlashContent
      : activeCampaign.Content

  return (
    <div className="flash">
      <div className="flash-content">
        <ActiveContent />
      </div>
    </div>
  )
}
